import { DBSchema, deleteDB, IDBPDatabase, openDB } from 'idb';
import {
  RapidCaptureAuthority,
  RapidCaptureDraft,
  RapidCaptureRecord,
  RapidCaptureStats,
  sanitizeRapidCaptureTerminalRecord,
} from './rapidCaptureTypes';
import { validateKeywords } from '../../domain/keywords';
import { recordRapidCaptureMetric } from './rapidCaptureMetrics';
import { processImage } from '../../services/imageProcessing';

export const RAPID_CAPTURE_DB_NAME = 'qkindex-rapid-capture';
export const RAPID_CAPTURE_DB_VERSION = 5;
export const RAPID_CAPTURE_STORE = 'captures';
export const RAPID_CAPTURE_CHANGED_EVENT = 'qkindex:rapid-capture-changed';
export const MAX_PENDING_CAPTURES = 100;
export const MAX_PENDING_BYTES = 250 * 1024 * 1024;
const AUTHORITY_PURGE_PREFIX = 'qkindex.pending-authority-purge.';

interface AuthorityPurgeTombstone {
  userId: string;
  collectionId: string;
  observedMembershipVersion: number;
  reason: 'revoked' | 'membership-changed' | 'collection-deleting';
}

interface RapidCaptureSchema extends DBSchema {
  captures: {
    key: string;
    value: RapidCaptureRecord;
    indexes: {
      'by-user': string;
      'by-user-state': [string, string];
      'by-authority': [string, string];
      'by-authority-state': [string, string, string];
    };
  };
}

let databasePromise: Promise<IDBPDatabase<RapidCaptureSchema>> | null = null;

const database = () => {
  if (typeof indexedDB === 'undefined')
    throw new Error('Durable browser storage is unavailable.');
  if (!databasePromise) {
    databasePromise = openDB<RapidCaptureSchema>(
      RAPID_CAPTURE_DB_NAME,
      RAPID_CAPTURE_DB_VERSION,
      {
        upgrade(db, oldVersion, _newVersion, transaction) {
          if (!db.objectStoreNames.contains(RAPID_CAPTURE_STORE)) {
            const store = db.createObjectStore(RAPID_CAPTURE_STORE, {
              keyPath: 'operationId',
            });
            store.createIndex('by-user', 'userId');
            store.createIndex('by-user-state', ['userId', 'state']);
            store.createIndex('by-authority', ['userId', 'collectionId']);
            store.createIndex('by-authority-state', [
              'userId',
              'collectionId',
              'state',
            ]);
          } else if (oldVersion < 2) {
            const store = transaction.objectStore(RAPID_CAPTURE_STORE);
            if (!store.indexNames.contains('by-authority')) {
              store.createIndex('by-authority', ['userId', 'collectionId']);
            }
            if (!store.indexNames.contains('by-authority-state')) {
              store.createIndex('by-authority-state', [
                'userId',
                'collectionId',
                'state',
              ]);
            }
            // Keep pending photos from older clients for explicit recovery.
          }
          if (db.objectStoreNames.contains('metadata' as never))
            db.deleteObjectStore('metadata' as never);
        },
      },
    );
  }
  return databasePromise;
};

const changed = () => {
  if (typeof window !== 'undefined')
    window.dispatchEvent(new Event(RAPID_CAPTURE_CHANGED_EVENT));
};

const recordBytes = (record: Pick<RapidCaptureRecord, 'image'>) =>
  record.image.size +
  ((record as unknown as { labelScanImage?: Blob }).labelScanImage?.size || 0);

const authorityPurgeKey = (userId: string, collectionId: string) =>
  `${AUTHORITY_PURGE_PREFIX}${encodeURIComponent(userId)}.${encodeURIComponent(collectionId)}`;

export const rapidCaptureDb = {
  async list(userId: string): Promise<RapidCaptureRecord[]> {
    const records = await (
      await database()
    ).getAllFromIndex(RAPID_CAPTURE_STORE, 'by-user', userId);
    return records.sort((left, right) =>
      left.createdAt.localeCompare(right.createdAt),
    );
  },

  async get(operationId: string): Promise<RapidCaptureRecord | undefined> {
    return (await database()).get(RAPID_CAPTURE_STORE, operationId);
  },

  async stats(
    userId: string,
    collectionId?: string,
  ): Promise<RapidCaptureStats> {
    const records = (await this.list(userId)).filter(
      (record) => !collectionId || record.collectionId === collectionId,
    );
    return {
      pending: records.filter(({ state }) => state === 'pending').length,
      active: records.filter(({ state }) => state === 'submitting').length,
      analysisProcessing: records.filter(
        ({ state }) => state === 'analysis-submitting',
      ).length,
      confirming: records.filter(({ state }) => state === 'confirming').length,
      failed: records.filter(
        ({ state }) => state === 'failed' || state === 'terminal',
      ).length,
      total: records.length,
      bytes: records.reduce((total, record) => total + recordBytes(record), 0),
    };
  },

  async enqueue(
    authority: RapidCaptureAuthority,
    draft: RapidCaptureDraft,
    acceptedAiCostVersion?: string,
    aiSkipReason?: RapidCaptureRecord['aiSkipReason'],
    retainedItemId?: string,
  ): Promise<RapidCaptureRecord> {
    if (
      !authority.userId ||
      !authority.collectionId ||
      authority.compatibilityMode !== 'collection-only' ||
      !Number.isSafeInteger(authority.membershipVersion) ||
      authority.membershipVersion < 0
    ) {
      throw new Error('Rapid Capture authority is unavailable.');
    }
    const startedAt = performance.now();
    const location = draft.location.trim();
    if (!location)
      throw new Error('Choose a location before accepting a capture.');
    if (!Number.isFinite(draft.qty) || draft.qty < 1) {
      throw new Error('Quantity must be at least 1.');
    }
    const image = await processImage(
      draft.image instanceof File
        ? draft.image
        : new File([draft.image], 'capture.jpg', { type: draft.image.type }),
    );
    const stats = await this.stats(authority.userId);
    const incomingBytes = image.size;
    if (
      stats.total >= MAX_PENDING_CAPTURES ||
      stats.bytes + incomingBytes > MAX_PENDING_BYTES
    ) {
      throw new Error(
        'The Rapid Capture queue is full. Let queued items upload before capturing more.',
      );
    }
    const operationId = crypto.randomUUID();
    const analysisOperationId = draft.analyzeImage
      ? crypto.randomUUID()
      : undefined;
    const now = new Date().toISOString();
    const record: RapidCaptureRecord = {
      schemaVersion: 5,
      operationId,
      userId: authority.userId,
      collectionId: authority.collectionId,
      membershipVersion: authority.membershipVersion,
      ...(authority.lifecycleVersion === undefined
        ? {}
        : { lifecycleVersion: authority.lifecycleVersion }),
      compatibilityMode: authority.compatibilityMode,
      itemId: retainedItemId || crypto.randomUUID(),
      image,
      analyzeImage: draft.analyzeImage,
      keywords: validateKeywords(draft.keywords),
      location,
      locationId: draft.locationId,
      qty: draft.qty,
      ...(draft.tagIds === undefined ? {} : { tagIds: [...draft.tagIds] }),
      state: 'pending',
      itemCreated: false,
      itemCreateAttempted: false,
      attempts: 0,
      createdAt: now,
      updatedAt: now,
      analysisOperationId,
      ...(analysisOperationId && acceptedAiCostVersion
        ? { acceptedAiCostVersion }
        : {}),
      ...(aiSkipReason ? { aiSkipReason } : {}),
    };
    const db = await database();
    const transaction = db.transaction(RAPID_CAPTURE_STORE, 'readwrite');
    await transaction.store.put(record);
    await transaction.done;
    recordRapidCaptureMetric(
      'indexeddb-enqueue',
      performance.now() - startedAt,
      {
        bytes: incomingBytes,
        analysis: Boolean(draft.analyzeImage),
      },
    );
    changed();
    return record;
  },

  async put(record: RapidCaptureRecord): Promise<void> {
    const db = await database();
    const transaction = db.transaction(RAPID_CAPTURE_STORE, 'readwrite');
    const existing = await transaction.store.get(record.operationId);
    if (
      existing &&
      (existing.userId !== record.userId ||
        existing.collectionId !== record.collectionId ||
        existing.membershipVersion !== record.membershipVersion ||
        existing.compatibilityMode !== record.compatibilityMode)
    ) {
      throw new Error('Rapid Capture queue authority is immutable.');
    }
    if (existing?.state === 'terminal' && record.state !== 'terminal') {
      await transaction.done;
      throw new Error('Rapid Capture terminal state is immutable.');
    }
    await transaction.store.put({
      ...record,
      updatedAt: new Date().toISOString(),
    });
    await transaction.done;
    changed();
  },

  /** Merge a receipt into the current entry atomically; never revive removed or revoked work. */
  async update(
    identity: RapidCaptureRecord,
    reduce: (current: RapidCaptureRecord) => RapidCaptureRecord | undefined,
  ): Promise<RapidCaptureRecord | undefined> {
    const transaction = (await database()).transaction(
      RAPID_CAPTURE_STORE,
      'readwrite',
    );
    const current = await transaction.store.get(identity.operationId);
    if (
      !current ||
      current.state === 'terminal' ||
      current.userId !== identity.userId ||
      current.collectionId !== identity.collectionId ||
      current.itemId !== identity.itemId ||
      current.membershipVersion !== identity.membershipVersion ||
      current.lifecycleVersion !== identity.lifecycleVersion ||
      current.compatibilityMode !== identity.compatibilityMode
    ) {
      await transaction.done;
      return undefined;
    }
    const next = reduce(current);
    if (next === current) {
      await transaction.done;
      return current;
    }
    if (next)
      await transaction.store.put({
        ...next,
        updatedAt: new Date().toISOString(),
      });
    else await transaction.store.delete(identity.operationId);
    await transaction.done;
    changed();
    return next;
  },

  async remove(operationId: string): Promise<void> {
    await (await database()).delete(RAPID_CAPTURE_STORE, operationId);
    changed();
  },

  async resetForSession(userId: string, collectionId?: string): Promise<void> {
    const records = await this.list(userId);
    await Promise.all(
      records
        .filter(
          (record) => !collectionId || record.collectionId === collectionId,
        )
        .filter(
          ({ state }) =>
            state !== 'pending' &&
            state !== 'failed' &&
            state !== 'confirming' &&
            state !== 'terminal',
        )
        .map((record) => this.put({ ...record, state: 'pending' })),
    );
  },

  async terminalizeCollectionAuthority(
    userId: string,
    collectionId: string,
    observedMembershipVersion: number,
    reason: 'revoked' | 'membership-changed' | 'collection-deleting',
  ): Promise<number> {
    const db = await database();
    const records = await db.getAllFromIndex(
      RAPID_CAPTURE_STORE,
      'by-authority',
      [userId, collectionId],
    );
    const affected = records.filter(
      (record) =>
        (reason === 'collection-deleting' ||
          record.membershipVersion !== observedMembershipVersion) &&
        record.state !== 'terminal',
    );
    if (!affected.length) return 0;
    const transaction = db.transaction(RAPID_CAPTURE_STORE, 'readwrite');
    await Promise.all(
      affected.map((record) =>
        transaction.store.put(
          sanitizeRapidCaptureTerminalRecord(record, reason, {
            category: 'permission',
            message:
              reason === 'revoked'
                ? 'Collection membership was revoked.'
                : reason === 'collection-deleting'
                  ? 'Collection deletion has started.'
                  : 'Collection membership changed.',
          }),
        ),
      ),
    );
    await transaction.done;
    changed();
    return affected.length;
  },

  async terminalizeCollectionLifecycle(
    userId: string,
    collectionId: string,
    lifecycleVersion: number,
  ): Promise<number> {
    const db = await database();
    const records = await db.getAllFromIndex(
      RAPID_CAPTURE_STORE,
      'by-authority',
      [userId, collectionId],
    );
    const affected = records.filter(
      (record) =>
        record.lifecycleVersion !== lifecycleVersion &&
        record.state !== 'terminal',
    );
    if (!affected.length) return 0;
    const transaction = db.transaction(RAPID_CAPTURE_STORE, 'readwrite');
    await Promise.all(
      affected.map((record) =>
        transaction.store.put(
          sanitizeRapidCaptureTerminalRecord(record, 'collection-deleting', {
            category: 'permission',
            message: 'Collection lifecycle changed.',
          }),
        ),
      ),
    );
    await transaction.done;
    changed();
    return affected.length;
  },

  async deleteCollectionAuthority(
    userId: string,
    collectionId: string,
    observedMembershipVersion: number,
    includeCurrentVersion = false,
  ): Promise<number> {
    const db = await database();
    const records = await db.getAllFromIndex(
      RAPID_CAPTURE_STORE,
      'by-authority',
      [userId, collectionId],
    );
    const affected = records.filter(
      (record) =>
        includeCurrentVersion ||
        record.membershipVersion !== observedMembershipVersion,
    );
    if (!affected.length) return 0;
    const transaction = db.transaction(RAPID_CAPTURE_STORE, 'readwrite');
    await Promise.all(
      affected.map(({ operationId }) => transaction.store.delete(operationId)),
    );
    await transaction.done;
    changed();
    return affected.length;
  },

  async purgeCollectionAuthority(
    userId: string,
    collectionId: string,
    observedMembershipVersion: number,
    reason: 'revoked' | 'membership-changed' | 'collection-deleting',
  ): Promise<number> {
    if (typeof localStorage === 'undefined') {
      throw new Error('Durable authority cleanup state is unavailable.');
    }
    const tombstone: AuthorityPurgeTombstone = {
      userId,
      collectionId,
      observedMembershipVersion,
      reason,
    };
    const key = authorityPurgeKey(userId, collectionId);
    let tombstonePersisted = false;
    try {
      localStorage.setItem(key, JSON.stringify(tombstone));
      tombstonePersisted = true;
    } catch {
      // Continue with exact sanitization even when marker persistence is unavailable.
    }
    let affected: number;
    try {
      affected = await this.terminalizeCollectionAuthority(
        userId,
        collectionId,
        observedMembershipVersion,
        reason,
      );
    } catch {
      affected = await this.deleteCollectionAuthority(
        userId,
        collectionId,
        observedMembershipVersion,
        reason === 'collection-deleting',
      );
    }
    if (tombstonePersisted) localStorage.removeItem(key);
    return affected;
  },

  async retryPendingAuthorityPurges(userId: string): Promise<number> {
    if (typeof localStorage === 'undefined') return 0;
    const prefix = `${AUTHORITY_PURGE_PREFIX}${encodeURIComponent(userId)}.`;
    const keys = Array.from({ length: localStorage.length }, (_, index) =>
      localStorage.key(index),
    )
      .filter((key): key is string => Boolean(key?.startsWith(prefix)))
      .slice(0, 100);
    let affected = 0;
    for (const key of keys) {
      let tombstone: AuthorityPurgeTombstone;
      try {
        tombstone = JSON.parse(
          localStorage.getItem(key) || '',
        ) as AuthorityPurgeTombstone;
      } catch {
        localStorage.removeItem(key);
        continue;
      }
      if (
        tombstone.userId !== userId ||
        !tombstone.collectionId ||
        !Number.isSafeInteger(tombstone.observedMembershipVersion) ||
        tombstone.observedMembershipVersion < 0 ||
        !['revoked', 'membership-changed', 'collection-deleting'].includes(
          tombstone.reason,
        )
      ) {
        localStorage.removeItem(key);
        continue;
      }
      affected += await this.purgeCollectionAuthority(
        tombstone.userId,
        tombstone.collectionId,
        tombstone.observedMembershipVersion,
        tombstone.reason,
      );
    }
    return affected;
  },

  async reconcileCollectionAuthorities(
    userId: string,
    authorities: readonly {
      collectionId: string;
      membershipVersion?: number;
      deleting?: boolean;
      lifecycleVersion?: number;
    }[],
  ): Promise<number> {
    let affected = 0;
    for (const authority of authorities.slice(0, MAX_PENDING_CAPTURES)) {
      if (authority.lifecycleVersion !== undefined) {
        affected += await this.terminalizeCollectionLifecycle(
          userId,
          authority.collectionId,
          authority.lifecycleVersion,
        );
      }
      if (authority.deleting && authority.membershipVersion !== undefined) {
        affected += await this.purgeCollectionAuthority(
          userId,
          authority.collectionId,
          authority.membershipVersion,
          'collection-deleting',
        );
      } else if (authority.membershipVersion === undefined) {
        affected += await this.terminalizeCollectionAuthority(
          userId,
          authority.collectionId,
          -1,
          'revoked',
        );
      } else {
        affected += await this.terminalizeCollectionAuthority(
          userId,
          authority.collectionId,
          authority.membershipVersion,
          'membership-changed',
        );
      }
    }
    return affected;
  },

  subscribe(listener: () => void): () => void {
    window.addEventListener(RAPID_CAPTURE_CHANGED_EVENT, listener);
    return () =>
      window.removeEventListener(RAPID_CAPTURE_CHANGED_EVENT, listener);
  },
};

export const resetRapidCaptureDatabaseForTests = async () => {
  if (databasePromise) (await databasePromise).close();
  databasePromise = null;
  await deleteDB(RAPID_CAPTURE_DB_NAME);
};
